// Удержания за месяц: кому сколько аванса, билетов, формы, обучения.
//
// ## Откуда взялось
//
// 24.09.2026 руководитель заказчика попросил «строку удержания, в которую можно провалиться и
// там будут Авансы/билет/форма/обучения и т.д.», «чтоб я мог открыть и посмотреть, кому сколько
// авансов и прочих удержаний выдали за период». До этого ответ собирался перебором ведомостей
// объектов: у каждой свои колонки, и сложить «сколько формы удержали за август у всех» было
// нечем.
//
// ## Где живёт и почему
//
// Вкладкой «Расчёта» рядом с «Долгами», а не отдельной строкой в полосе разделов: разделов в
// программе семь, и новое появляется представлением внутри раздела (`ПРАВИЛА.md`, 1.1).
// Удержания — это суммы ведомостей месяца, и отборы у них те же: месяц и юрлицо.
//
// ## Числа — из ведомостей, до рубля
//
// Сервер складывает те же суммы, что стоят в колонках ведомостей месяца. Второй источник того
// же числа (погашения долгов) разошёлся бы с ведомостью: погашение появляется только при
// утверждении, а удержания спрашивают и у неутверждённой.
//
// ## Месяц по умолчанию — расчётный
//
// Тот, за который сейчас выдают зарплату, — прошлый: за текущий ведомостей обычно ещё нет, и
// вкладка открывалась бы пустой (то же правило, что у «Дел» и расчётного листа).

import { Link, useNavigate } from 'react-router';

import { RiScales3Line } from '@remixicon/react';

import type { DeductionsReportOut } from '@/shared/api/endpoints/reports';
import { useAuthStore } from '@/shared/auth/store';
import type { СостояниеПереноса } from '@/shared/debts/перенос';
import {
  АДРЕС_ПЕРЕНОСА,
  ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ,
  КАК_ПЕРЕНЕСТИ_ЗДЕСЬ,
  useПереносДолгов,
  состояниеПереноса,
} from '@/shared/debts/перенос';
import type { ApiError } from '@/shared/errors/api-error';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { useLegalEntitiesQuery } from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import { PrintButton } from '@/shared/reports/PrintButton';
import type { SearchState } from '@/shared/routing/search';
import { buildSearch } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import type { Period } from '@/shared/time/period';
import {
  formatPeriodKey,
  formatPeriodTitle,
  месяцИзАдресаИлиРасчётный,
  shiftPeriod,
} from '@/shared/time/period';
import { ListRow } from '@/shared/ui/list-row';
import { PeriodStepper } from '@/shared/ui/period-stepper';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { Toolbar } from '@/shared/ui/toolbar';

import { ListPane } from '@/layout/ListPane';

import { useDeductions } from '../queries';
import { FILTER_DEDUCTION, FILTER_LEGAL_ENTITY, FILTER_PERIOD, VIEW_SHEETS } from '../route';

const ЛЮДИ = ['человек', 'человека', 'человек'] as const;

/** Месяц вкладки: из адреса, а без него — расчётный (прошлый), как и у вкладки «Ведомости». */
export function месяцУдержаний(search: SearchState, сейчас: Date = new Date()): Period {
  return месяцИзАдресаИлиРасчётный(search.filters[FILTER_PERIOD], сейчас);
}

/**
 * Почему удержаний нет — словами и с тем, что сделать. `null` — они есть.
 *
 * Три разных случая, и выглядят они по-разному (правило 7): ведомостей за месяц нет вовсе;
 * ведомости есть, но не посчитаны; посчитаны — и не удержано ничего. Ноль в последнем случае
 * правдивый, в первых двух его нет.
 *
 * В последнем случае причина часто одна и та же: старые долги ещё не перенесены, и удерживать
 * по ним нечего. Так было у заказчика 24.09.2026 — ведомости за август посчитаны, долгов в
 * программе ноль. «Проверьте долг человека» в этот момент отправляло искать то, чего нет.
 */
export function почемуПусто(
  отчёт: DeductionsReportOut,
  месяц: string,
  перенос: СостояниеПереноса = 'неизвестно',
): { title: string; description: string } | null {
  if (отчёт.sheets === 0) {
    return {
      title: `За ${месяц} ведомостей нет`,
      description:
        'Удержания берутся из ведомостей месяца: аванс, билеты, форма, обучение и остальное — из тех же колонок. Создайте ведомости во вкладке «Ведомости», и удержания появятся здесь после расчёта.',
    };
  }
  if (отчёт.sheets_calculated === 0) {
    return {
      title: `Ведомости за ${месяц} ещё не посчитаны`,
      description:
        'Удержания появятся после расчёта: во вкладке «Ведомости» нажмите «Пересчитать весь период».',
    };
  }
  if (отчёт.items.length === 0) {
    return {
      title: `За ${месяц} ничего не удержано`,
      description:
        перенос === 'не перенесены'
          ? `В посчитанных ведомостях месяца нет ни аванса, ни удержаний по долгам. ${ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ.replace('сюда ещё не перенесены.', 'ещё не перенесены, и удерживать по ним пока нечего.')} ${КАК_ПЕРЕНЕСТИ_ЗДЕСЬ}`
          : 'В посчитанных ведомостях месяца нет ни аванса, ни удержаний по долгам. Это ответ расчёта, а не пропуск: если удержание ждали — проверьте долг человека и его график.',
    };
  }
  return null;
}

export function DeductionsScreen({ search }: { search: SearchState }) {
  const navigate = useNavigate();
  const период = месяцУдержаний(search);
  const юрлицо = search.filters[FILTER_LEGAL_ENTITY];
  const вид = search.filters[FILTER_DEDUCTION];
  const отчёт = useDeductions(период, юрлицо);
  const юрлица = useLegalEntitiesQuery();
  const долгиВидны = can(
    useAuthStore((state) => state.permissions),
    'core.debt.view',
  );
  const перенос = состояниеПереноса(useПереносДолгов(долгиВидны));
  const месяцСловами = formatPeriodTitle(период).toLowerCase();

  function отбор(ключ: string, значение: string | null): string {
    const фильтры = { ...search.filters };
    if (значение === null) {
      delete фильтры[ключ];
    } else {
      фильтры[ключ] = значение;
    }
    return buildSearch({ ...search, filters: фильтры, panels: [] });
  }

  function сменитьМесяц(следующий: Period) {
    void navigate({
      search: buildSearch({
        ...search,
        filters: { ...search.filters, [FILTER_PERIOD]: formatPeriodKey(следующий) },
        panels: [],
      }),
    });
  }

  return (
    <main className='flex min-h-0 min-w-0 flex-1 flex-col bg-bg-white-0'>
      <Toolbar>
        <PeriodStepper
          label='Месяц'
          title={formatPeriodTitle(период)}
          onPrev={() => сменитьМесяц(shiftPeriod(период, -1))}
          onNext={() => сменитьМесяц(shiftPeriod(период, 1))}
        />
        <div className='min-w-56'>
          <SelectField
            id='deductions-entity'
            label='Юрлицо'
            value={юрлицо ?? NO_VALUE}
            options={(юрлица.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.short_name ?? строка.name,
            }))}
            noneLabel='все юрлица'
            placeholder='все юрлица'
            pending={юрлица.isPending}
            failed={юрлица.isError}
            failedError={юрлица.error as ApiError | null}
            onChange={(значение) =>
              void navigate({
                search: отбор(FILTER_LEGAL_ENTITY, значение === NO_VALUE ? null : значение),
              })
            }
          />
        </div>
        <PrintButton />
      </Toolbar>

      <ScreenState
        data={отчёт.data}
        error={отчёт.error}
        subject='удержания за месяц'
        isEmpty={(данные) => почемуПусто(данные, месяцСловами) !== null}
        onRetry={() => void отчёт.refetch()}
        loading={
          <div className='flex flex-col gap-2 p-5'>
            <Skeleton className='h-20 w-full' />
            <Skeleton className='h-20 w-full' />
          </div>
        }
        empty={
          отчёт.data === undefined ? null : (
            <div className='p-5'>
              <EmptyState
                icon={RiScales3Line}
                {...(почемуПусто(отчёт.data, месяцСловами, перенос) ?? {
                  title: '',
                  description: '',
                })}
                action={
                  отчёт.data.sheets === 0 || отчёт.data.sheets_calculated === 0 ? (
                    <Link
                      to={`/payroll/${VIEW_SHEETS}?f.${FILTER_PERIOD}=${formatPeriodKey(период)}`}
                      className='цель-тача relative text-label-sm text-primary-base underline-offset-4 hover:underline'
                    >
                      Открыть ведомости за {месяцСловами}
                    </Link>
                  ) : перенос === 'не перенесены' ? (
                    <Link
                      to={АДРЕС_ПЕРЕНОСА}
                      className='цель-тача relative text-label-sm text-primary-base underline-offset-4 hover:underline'
                    >
                      Перенести остатки долгов
                    </Link>
                  ) : undefined
                }
              />
            </div>
          )
        }
      >
        {(данные) => (
          <Удержания
            данные={данные}
            вид={вид}
            месяцСловами={месяцСловами}
            адресВида={(код) => отбор(FILTER_DEDUCTION, код)}
          />
        )}
      </ScreenState>
    </main>
  );
}

function Удержания({
  данные,
  вид,
  месяцСловами,
  адресВида,
}: {
  данные: DeductionsReportOut;
  вид: string | undefined;
  месяцСловами: string;
  адресВида: (код: string | null) => string;
}) {
  const выбран = данные.kinds.find((строка) => строка.code === вид);
  const названия = Object.fromEntries(данные.kinds.map((строка) => [строка.code, строка.title]));
  // Люди выбранного вида — по его сумме; без выбора — все, по сумме всех удержаний.
  const люди =
    выбран === undefined
      ? данные.items
      : данные.items
          .filter((человек) => (человек.amounts[выбран.code] ?? 0) !== 0)
          .sort(
            (а, б) =>
              (б.amounts[выбран.code] ?? 0) - (а.amounts[выбран.code] ?? 0) ||
              а.employee_name.localeCompare(б.employee_name, 'ru'),
          );
  const неВсеПосчитаны = данные.sheets_calculated < данные.sheets;

  return (
    <div className='flex min-h-0 flex-1'>
      <ListPane
        caption={`Удержано за ${месяцСловами}`}
        note={
          неВсеПосчитаны ? (
            <span className='text-paragraph-xs text-text-sub-600'>
              Посчитано ведомостей: {данные.sheets_calculated} из {данные.sheets}. В остальных
              удержания появятся после расчёта.
            </span>
          ) : undefined
        }
      >
        <ul className='flex flex-col'>
          <li>
            <ListRow
              to={адресВида(null)}
              selected={выбран === undefined}
              title='Все удержания'
              subtitle={`${formatRubles(данные.total)} · ${countLabel(данные.items.length, ЛЮДИ)}`}
            />
          </li>
          {данные.kinds.map((строка) => (
            <li key={строка.code}>
              <ListRow
                to={адресВида(строка.code)}
                selected={строка.code === выбран?.code}
                title={строка.title}
                subtitle={`${formatRubles(строка.total)} · ${countLabel(строка.people, ЛЮДИ)}`}
              />
            </li>
          ))}
        </ul>
      </ListPane>

      <section className='flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto'>
        <header className='flex flex-col gap-0.5 border-b border-stroke-soft-200 px-5 py-3'>
          <h2 className='text-label-md text-text-strong-950'>
            {выбран === undefined ? 'Все удержания' : выбран.title}: {месяцСловами}
          </h2>
          <span className='text-paragraph-xs text-text-sub-600'>
            {выбран === undefined
              ? `${formatRubles(данные.total)} у ${countLabel(данные.items.length, ЛЮДИ)}. Числа — из колонок ведомостей месяца.`
              : `${formatRubles(выбран.total)} у ${countLabel(выбран.people, ЛЮДИ)}. Числа — из колонки «${выбран.title}» ведомостей месяца.`}
          </span>
        </header>
        <table className='w-full text-paragraph-sm'>
          <thead>
            <tr className='border-b border-stroke-soft-200 text-left text-paragraph-xs text-text-sub-600'>
              <th className='px-5 py-2 font-normal'>Человек</th>
              <th className='px-5 py-2 font-normal'>Где удержано</th>
              {выбран === undefined && <th className='px-5 py-2 font-normal'>Из чего</th>}
              <th className='px-5 py-2 text-right font-normal'>Удержано, ₽</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-stroke-soft-200'>
            {люди.map((человек) => (
              <tr key={человек.employee_id}>
                <td className='px-5 py-2.5'>
                  <Link
                    to={`/people/employees/r/${человек.employee_id}`}
                    className='цель-тача relative text-text-strong-950 underline-offset-4 hover:underline'
                  >
                    {человек.employee_name}
                  </Link>
                </td>
                <td className='px-5 py-2.5 text-text-sub-600'>{человек.sites.join(', ')}</td>
                {выбран === undefined && (
                  <td className='px-5 py-2.5 text-text-sub-600'>
                    {Object.entries(человек.amounts)
                      .map(([код, сумма]) => `${названия[код] ?? код} ${formatKopecks(сумма)}`)
                      .join(' · ')}
                  </td>
                )}
                <td className='px-5 py-2.5 text-right tabular-nums text-text-strong-950'>
                  {formatKopecks(
                    выбран === undefined ? человек.total : (человек.amounts[выбран.code] ?? 0),
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
